import zenScroll from "zenscroll";
import config from '@/js/config/app.json';
import { TotalPanelItem, LineCollection } from '@/interfaces/DTOs/LineEdition';
import { Line, LineRemark } from "@/interfaces/DTOs/Models";
import { EstimatePermissionKeys, EstimatePermissions } from "@/interfaces/DTOs/Permissions";
import { AlpineData, LivewireData, LivewireMethods } from "@/js/interfaces/Components/alpinejs/Estimate/EstimateLineReader";
import { forceEnglishFloatNotation, isValidFloat, sanitizeHtmlTag, translate } from "@/js/app/support/utils";
import { AlpineWired } from "@nolikein/types-livewire3";
import { createAlpineLivewireComponent } from "@nolikein/typed-alpinejs";
import is from "@/js/app/support/is";
import { InvalidLivewireResponseArgument, InvalidLivewireResponseFormat, InvalidWindowContentError } from "@/js/app/exceptions";
import { notifyErrorAlert, notifySuccessAlert } from "@/js/app/support/alert-and-banner";
import LineBag from "@/js/app/services/EstimateLines/LineBag";
import { EstimateRemark } from "@/interfaces/DTOs/Models";
import { EstimateRemarkBag } from "@/js/app/services/EstimateLines/EstimateRemarkBag";
import { LineRemarkBag } from "@/js/app/services/EstimateLines/LineRemarkBag";
import { PriceDisplayBag } from "@/js/app/services/EstimateLines/PriceDisplayBag";
import { PriceDisplay } from "@/js/app/services/EstimateLines/PriceDisplay";
import { LineRemarkBufferBag } from "@/js/app/services/EstimateLines/LineRemarkBufferBag";
import { MessageBag, MessageBagManager } from "@/js/app/services/EstimateLines/MessageBagManager";
import { EventManager } from "@/js/app/services/EventManager";

type Self = AlpineWired.AlpineComponentSelf<AlpineData, LivewireData, LivewireMethods>;

/**
 * Important note:
 * Methods with "call" prefix call the livewire component (backend) then update frontend.
 * Methods without this prefix update only the frontend.
 */
export default {
    name: "EstimateLineReader",
    component: (
        initialEstimateVersion: number,
        initialLineCount: number,
        lesserValueUnit: string,
    ) => createAlpineLivewireComponent<AlpineData, LivewireData, LivewireMethods>({
        /**
         * Properties
         */
        estimate: {
            created_at: "",
            purchase_order_id: null,
        },
        estimateVersion: initialEstimateVersion,

        // Line concerns
        lineBag: new LineBag(),
        priceFieldsDisplay: new PriceDisplayBag(lesserValueUnit),

        // Remarks concerns
        estimateRemarks: new EstimateRemarkBag(),
        lineRemarkBag: new LineRemarkBag(),

        // End total panel
        totalPanel: [],

        // Error display
        messageBag: new MessageBagManager(),

        // Page metadata
        lotsColor: {},
        staticItemsList: [],
        fileMaxSize: 0,

        // Permissions
        permissions: {
            mustGoToFirstProcess: false,
            canGoToNextProcess: false,
            canGoToFirstProcess: false,
            canEditEstimateLines: false,
            canReopenEstimate: false,
            canDeleteEstimate: false,
            canAbandonEstimate: false,
            canChangeStatus: false,
            canValidateOnCos: false,
            canCreateRemark: false,
        },

        // User inputs
        estimateRemarkUserInput: "",
        lineRemarkBuffer: new LineRemarkBufferBag(),

        // Front states
        currentPage: 1,
        gotAllLines: false,
        didInitialRequest: false,
        isUpdating: false,
        canGotoBottom: true,
        events: new EventManager(),

        /** Getters */
        get linePerPage(): number {
            return config.components.EstimateLineReader.linePerLoad;
        },

        get remarkMaxCount(): number {
            return config.components.EstimateLineReader.lineRemarkLimitedQuantity;
        },

        /** Methods */

        async init() {
            await this.performInitializationRequest(initialLineCount, this.linePerPage);

            // Decide what to do when a livewire call fails
            this.events.on('unhandled-promise', () => {
                this.isUpdating = false;
            });
        },

        destroy() {
            this.events.removeEventListeners();
        },

        /**
         * Perform the initial request to show lines.
         * @param linePerPage The count of lines to retrieve for a page
         */
        async performInitializationRequest(lineCount: number, linePerPage: number | null = null): Promise<void> {
            linePerPage ??= this.linePerPage;

            // Reset some variables to a default value
            this.lineBag = LineBag.fake(lineCount);
            this.gotAllLines = false;
            this.didInitialRequest = false;
            this.isUpdating = false;
            this.lineRemarkBuffer.clear();
            this.currentPage = 1;

            const data = await this.$wire.$call(
                "getInitializationData",
                this.estimateVersion,
                linePerPage,
                this.currentPage,
            );

            // Set data that often change between requests
            this.estimate = data.estimate;
            this.estimateRemarks = new EstimateRemarkBag(data.estimateRemarks);
            this.lineRemarkBag = new LineRemarkBag(data.lineRemarks);
            this.replaceLinesProperly(data.lines.items, this.currentPage);
            this.updateTotalPanelValues(data.totalPanel);
            this.messageBag.updateMessages(data.messageBag);
            this.updatePermissions(data.permissions);

            // Set data that never change
            this.lotsColor = data.lotsColor;
            this.staticItemsList = data.staticItemsList;
            this.fileMaxSize = data.fileMaxSize;

            // Tells the program we did the initial request
            this.didInitialRequest = true;

            // Trigger the load-until only if we didnt have all the lines.
            if (data.lines.hasMorePage) {
                this.loadLinesUntilLast();
            } else {
                this.gotAllLines = true;
            }
        },

        /**
         * Reload the Alpinejs component
         */
        reloadComponent(): void {
            this.disableAllPermissions();
            this.currentPage = 1;
            this.performInitializationRequest(this.lineBag.count(), this.linePerPage);
        },

        /**
         * Disable all permissions for the current document. Then, you need to refresh these informations.
         */
        disableAllPermissions(): void {
            Object.keys(this.permissions).forEach(key => {
                this.permissions[key as keyof typeof EstimatePermissionKeys] = false;
            });
        },

        /**
         * Refresh the estimate permissions, message bag, totalPanel.
         *
         * @param version The estimate version
         */
        async refreshEstimateMetadata(version: number): Promise<void> {
            try {
                const data = await this.$wire.$call("getFreshEstimateMetadata", version);
                this.updateTotalPanelValues(data.totalPanel);
                this.updatePermissions(data.permissions);
                this.messageBag.updateMessages(data.messageBag);
            } catch (error: unknown) {
                notifyErrorAlert(translate("An error occurred while refreshing the estimate metadata. Please reload the page"));
                if (error instanceof Error) {
                    throw error;
                }
            }
        },

        // ----------------------
        // Global public methods
        // ----------------------

        /**
         * Update the current permissions
         */
        updatePermissions(newPermissions: EstimatePermissions): void {
            Object.keys(newPermissions).forEach((key) => {
                // Need to fix the error here when there is no supposed to have an error
                if (key in this.permissions) {
                    this.permissions[key as keyof typeof EstimatePermissionKeys] = newPermissions[key as keyof typeof EstimatePermissionKeys];
                }
            });
        },


        // ---------------------
        // Other public methods
        // ---------------------

        /**
         * Retrieve a price display if exists for a line or an empty display
         *
         * @param lineIndex The line index to find
         */
        getPriceDisplayFromIndex(lineIndex: number): PriceDisplay {
            return this.priceFieldsDisplay.get(
                this.lineBag.getIdFromIndex(lineIndex),
            );
        },

        /**
         * Retrieve a price display from a line id
         *
         * @param lineId The line id for the price display to get
         */
        getPriceDisplayFromId(lineId: number): PriceDisplay {
            return this.priceFieldsDisplay.get(lineId);
        },

        /**
         * Change the current estimate version
         *
         * @param newVersion The estimate version to define
         * @param newLineCount The estimate dummy line number to show at line refresh
         */
        changeVersionAndRefresh(newVersion: number, newLineCount: number): Self {
            this.disableAllPermissions();
            this.estimateVersion = newVersion;
            this.currentPage = 1;

            this.performInitializationRequest(newLineCount, this.linePerPage);

            return this;
        },

        hasAnyLine(): boolean {
            return this.lineBag.isNotEmpty();
        },

        /**
         * Move a line from its old position to a new position
         *
         * @param lineId The line id
         * @param lineOldIndex The old line position
         * @param lineNewIndex The new line position
         */
        moveLine(lineId: number, lineOldIndex: number, lineNewIndex: number): void {
            // Reorder the dragged item in the internal list
            this.lineBag.moveLine(lineId, lineNewIndex);

            // Sync the new ordering with the backend
            this.callSyncLineOrdering(lineId, lineNewIndex);
        },

        /**
         * Call the backend to sync line order with given array.
         *
         * @param lineIdToMove The line id to move
         * @param newPosition The new position for the line
         */
        callSyncLineOrdering(lineIdToMove: number, newPosition: number): void {
            this.isUpdating = true;
            this.$wire
                .$call("reorderLines", lineIdToMove, newPosition)
                .then(() => {
                    this.isUpdating = false;
                });
        },

        /**
         * Scroll the page to the bottom content
         */
        scrollToEstimateRemarkSection(): void {
            const rect = this.getHTMLElementEstimateRemarks().getBoundingClientRect();

            zenScroll.toY(rect.top - 280);
        },

        /**
         * Retrieve the estimate-remarks tag
         */
        getHTMLElementEstimateRemarks(): HTMLElement {
            const el = document.getElementById("estimate-remarks");
            if (null === el) {
                throw new Error(`The html element with id "estimate-remarks" does not exists`);
            }
            return el;
        },

        /**
         * Checks if the user can scroll to the bottom
         */
        checkIfCanScrollToBottom(): void {
            const doc = document.documentElement;
            const top = (window.scrollY || doc.scrollTop) - (doc.clientTop || 0);

            const rect = this.getHTMLElementEstimateRemarks().getBoundingClientRect();

            this.canGotoBottom = top < rect.top;
        },


        /**
         * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
         * Line managment
         * <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<
         */

        /**
         * Get the lines from the line bag
         */
        getLines(): Array<Line> {
            return this.lineBag.all();
        },

        /**
         * Place lines into the lines bag.
         *
         * @param lines The lines to insert
         * @param page The current page
         */
        replaceLinesProperly(lines: Array<Line>, page: number): void {
            this.lineBag.replaceManyPaginated(lines, page, this.linePerPage);
            this.priceFieldsDisplay.addMany(lines);
        },

        /**
         * Retrieve the lines but paginated
         *
         * @param lineQuantity The count of lines to retrieve for a page
         * @param page The page to retrieve
         */
        async getPaginatedLines(lineQuantity: number = 10, page: number = 1): Promise<LineCollection> {
            return await this.$wire.$call(
                "getPaginatedLines",
                this.estimateVersion,
                lineQuantity,
                page,
            );
        },

        /**
         * Load lines until you loaded all lines.
         */
        async loadLinesUntilLast(): Promise<void> {
            this.currentPage++;
            const lines = await this.getPaginatedLines(
                this.linePerPage,
                this.currentPage,
            );
            this.replaceLinesProperly(lines.items, this.currentPage);

            if (lines.hasMorePage) {
                // Load more lines
                this.loadLinesUntilLast();
            } else {
                // Allows the user to update the lines
                this.gotAllLines = true;
            }
        },

        /**
         * Add a new line from an id
         *
         * @param lineId A line id to add (frontend)
         */
        addLineFromId(lineId: number): void {
            // Do something only if the line doesnt exists
            if (!this.lineBag.hasIndexFromId(lineId)) {
                this.$wire
                    .$call("getLine", lineId)
                    .then((data) => {
                        this.isUpdating = false;

                        // Guess that the object is valid
                        this.lineBag.add(data.line);
                        this.priceFieldsDisplay.add(data.line);

                        this.$dispatch("update-frontend-for-line-metadata", {
                            line: data.line,
                        });

                        // Update internal message bag
                        this.messageBag.updateMainAndLineMessages(lineId, data.messageBag);

                    });
            }
        },

        /**
         * Remove an estimate line (in frontend)
         *
         * @param lineId A line id to remove
         */
        hardDeleteLineFromId(lineId: number): void {
            const lineIndex = this.lineBag.getIndexFromId(lineId);
            this.hardDeleteLineFromIndex(lineIndex);
        },

        /**
         * Remove an estimate line (in frontend)
         *
         * @param lineIndex A line index to remove
         */
        hardDeleteLineFromIndex(lineIndex: number): void {
            // Remove the line from the array
            const lineId = this.lineBag.deleteFromIndex(lineIndex);

            if (this.lineRemarkBag.has(lineId)) {
                this.lineRemarkBag.emptyRemarksForLine(lineId);
            }
            this.messageBag.removeMessagesForLine(lineId);

            const updateBackend = false;
            if (updateBackend) {
                this.$wire.$call('reorderAllLines', this.estimateVersion);
            }
            this.refreshEstimateMetadata(this.estimateVersion);
        },

        /**
         * Soft delete a line from an index (frontend)
         *
         * @param lineIndex The line index to soft delete
         * @param ifSoftDeletedInNextVersion If soft deleted, delete it in next version
         */
        softDeleteLineFromIndex(lineIndex: number, ifSoftDeletedInNextVersion: boolean = false): void {
            this.lineBag.softDelete(lineIndex, ifSoftDeletedInNextVersion);
        },

        /**
         * Unsoft delete/restore a line from an index (frontend)
         *
         * @param lineIndex The line index to restore
         */
        restoreLineFromIndex(lineIndex: number): void {
            this.lineBag.restore(lineIndex);
        },

        /**
         * Unsoft delete/restore a line from an index (frontend)
         *
         * @param lineId The line id to restore
         */
        restoredLineFromId(lineId: number): void {
            const lineIndex = this.lineBag.getIndexFromId(lineId);

            this.restoreLineFromIndex(lineIndex);
            this.refreshEstimateMetadata(this.estimateVersion);
        },

        /**
         * Soft delete a line from an index (frontend and backend)
         *
         * @param lineIndex The line index to soft delete
         */
        async callSoftDeleteLineFromIndex(lineIndex: number): Promise<void> {
            const lineId = this.lineBag.getIdFromIndex(lineIndex);
            this.isUpdating = true;

            const data = await this.$wire.$call("softDeleteLine", lineId, true);
            this.isUpdating = false;

            this.updateTotalPanelValues(data.totalPanel);

            // Change the line display (frontend)
            this.softDeleteLineFromIndex(lineIndex);

            // Update internal message bag
            this.messageBag.updateMainAndLineMessages(lineId, data.messageBag);

            // Tell the top component to recalculate the global price
            this.$wire.$dispatch("calculateGlobalPrice");
        },

        /**
         * Unsoft delete/restore a line from an index (frontend and backend)
         *
         * @param lineIndex The line index to restore (in frontend and backend)
         */
        async callRestoreLineFromIndex(lineIndex: number): Promise<void> {
            const lineId = this.lineBag.getIdFromIndex(lineIndex);
            this.isUpdating = true;

            const data = await this.$wire.$call("softDeleteLine", lineId, false);
            this.isUpdating = false;

            this.updateTotalPanelValues(data.totalPanel);

            // Change the line display (frontend)
            this.restoreLineFromIndex(lineIndex);

            // Update internal message bag
            this.messageBag.updateMainAndLineMessages(lineId, data.messageBag);

            // Tell the top component to recalculate the global price
            this.$wire.$dispatch("calculateGlobalPrice");
        },

        /**
         * Trigger the openning of the line deletion modal
         *
         * @param lineIndex The line index you wanna delete
         */
        openLineDeletionModal(lineIndex: number): void {
            this.isUpdating = true;
            this.$wire
                .$call("setLineToDeleteIt", this.lineBag.getIdFromIndex(lineIndex))
                .then(() => {
                    this.isUpdating = false;
                }).catch((e: Error) => {
                    this.isUpdating = false;
                    throw e;
                });
        },

        /**
         * Hard delete, soft delete or restore a line in backend and frontend from a line index.
         *
         * @param lineIndex The line index in the list (frontend and backend)
         */
        async callSoftOrHardDeleteOrRestoreLineFromIndex(lineIndex: number): Promise<void> {
            // If the line is not new from this version, we could soft delete it
            if (this.lineBag.isExistingBeforeCurrentVersion(lineIndex)) {
                // We can know if a line should be soft deleted or restore from the not_in_next_version property.
                if (this.lineBag.shouldSoftDelete(lineIndex)) {
                    await this.callSoftDeleteLineFromIndex(lineIndex);
                } else {
                    await this.callRestoreLineFromIndex(lineIndex);
                }
                // We should trigger the SelectSlipItems livewire component and tells him to reload its collection
                this.$wire.$dispatch("SelectSlipItems", "updateSlipList");
            } else {
                // Otherwise, we should delete it
                this.openLineDeletionModal(lineIndex);
            }
        },

        /**
         * Soft delete/delete a line in frontend.
         *
         * @param lineId The line id to delete/soft delete (frontend)
         * @param shouldSoftDelete Tells if should hard of soft delete a line
         * @param ifSoftDeleteItInNextVersion If soft deleted, delete it in next version
         */
        softOrHardDeleteLineFromId(lineId: number, shouldSoftDelete: boolean, ifSoftDeleteItInNextVersion: boolean): void {
            if (!this.lineBag.hasIndexFromId(lineId)) {
                notifyErrorAlert(translate("The line you are trying to delete does not exists anymore. You can reload the page"));
                return;
            }
            const lineIndex = this.lineBag.getIndexFromId(lineId);

            if (shouldSoftDelete) {
                this.softDeleteLineFromIndex(
                    lineIndex,
                    ifSoftDeleteItInNextVersion,
                );
            } else {
                this.hardDeleteLineFromIndex(lineIndex);
            }
            this.refreshEstimateMetadata(this.estimateVersion);
        },

        /**
         * Describe what happen when line is updated frontside
         *
         * @param lineIndex
         */
        markLineAsUpdatedFromIndex(lineIndex: number): void {
            this.lineBag.setLineAsDirty(lineIndex);
        },

        /**
         * Toggle the action to override a line price from a line index.
         *
         * @param lineIndex The line index to "override"
         */
        callToggleOverridePriceFromIndex(lineIndex: number): void {
            const lineId = this.lineBag.getIdFromIndex(lineIndex);
            if (null === lineId) {
                throw new Error(`Line not found from index. Given index "${lineIndex}"`);
            }


            this.isUpdating = true;
            this.$wire
                .$call("toggleOverridePrice", lineId)
                .then((data) => {
                    this.isUpdating = false;

                    // Update the line
                    this.lineBag.replace(lineIndex, data.updatedLine);
                    this.priceFieldsDisplay.add(data.updatedLine);

                    this.$dispatch("update-frontend-for-line-metadata", {
                        line: data.updatedLine,
                    });

                    // Update the total
                    this.updateTotalPanelValues(data.totalPanel);

                    // Update internal message bag
                    this.messageBag.updateMainAndLineMessages(lineId, data.messageBag);

                    this.updatePermissions(data.permissions);
                });
        },

        /**
         * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
         * Line remark managment
         * <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<
         */

        formatLineRemark(remark: string | null) {
            return is.string(remark)
                ? sanitizeHtmlTag(remark).replace(/\n/g, '<br>')
                : '';
        },

        getLineRemarkInputElementFromLineId(lineId: number): HTMLInputElement {
            const el = document.getElementById(`remark-${lineId}`) as HTMLInputElement | null;
            if (null === el) {
                throw new Error('Cannot retrieve item with id ' + `remark-${lineId}`);
            }
            return el;
        },

        /**
         * Add a line remark to the lineRemarks container
         * @param lineId The line id to update
         * @param remark The remark message to update
         */
        addLineRemark(lineId: number, remark: LineRemark): void {
            this.lineRemarkBag.add(lineId, remark);
            this.lineBag.setLineIdAsDirty(lineId);
        },

        /**
         * Checks if there is any error for a line remark input
         *
         * @param lineId The line remark id in the message bag
         */
        hasAnyLineRemarkError(lineId: number): boolean {
            return this.lineRemarkBuffer.hasAnyError(lineId);
        },

        /**
         * Retrieve any error for a line remark input
         *
         * @param lineId The line remark id in the message bag
         */
        getLineRemarkErrors(lineId: number): string {
            return this.lineRemarkBuffer.getErrorsAsString(lineId);
        },

        /**
         * Remove a line remark error.
         *
         * @param lineId Line id
         */
        removeLineRemarkError(lineId: number): void {
            this.lineRemarkBuffer.delete(lineId);
        },

        /**
         * Remove a line remark from an id (frontend and backend)
         *
         * @param lineRemarkId The line remark id to remove
         */
        callRemoveLineRemarkFromId(lineRemarkId: number, lineId: number): void {
            this.isUpdating = true;

            this.lineRemarkBag.delete(lineId, lineRemarkId);
            this.$wire
                .$call("deleteLineRemarkFromId", lineRemarkId, lineId)
                .then((data) => {
                    this.isUpdating = false;

                    // Update internal message bag
                    this.messageBag.updateMainAndLineMessages(lineId, data.messageBag);
                    this.updatePermissions(data.permissions);
                });
        },

        /**
         * Retrieve line remarks conditionnaly
         *
         * @param lineId The line id to find where the remark is stored
         * @param showAll Choose to show all lines remarks
         */
        getLineRemarks(lineId: number, showAll: boolean): Array<LineRemark> {
            if (this.lineRemarkBag.isEmptyForLine(lineId)) {
                return [];
            }
            return showAll
                ? this.lineRemarkBag.get(lineId)
                : this.lineRemarkBag.take(lineId, this.remarkMaxCount);
        },

        hasLineRemarkLimitedResult(lineId: number): boolean {
            return this.lineRemarkBag.isLineHasMoreThanQuantityOfRemarks(lineId, this.remarkMaxCount);
        },

        /**
         * Update our buffer memory for line remarks so after we can send it to the backend.
         *
         * @param lineId The line id to update (in backend)
         */
        updateLineRemarkBufferMemory(lineId: number): void {
            const value = this.getLineRemarkInputElementFromLineId(lineId).value;
            this.lineRemarkBuffer.set(lineId, value);

            this.lineRemarkBuffer.deleteErrors(lineId);
            if (value.length < config.components.EstimateLineReader.lineRemarkInput.min) {
                this.lineRemarkBuffer.addError(
                    lineId,
                    translate(`Your remark should be higher than :min characters`, {
                        'min': config.components.EstimateLineReader.lineRemarkInput.min.toString(),
                    }),
                );
            }


            if (value.length > config.components.EstimateLineReader.lineRemarkInput.max) {
                this.lineRemarkBuffer.addError(
                    lineId,
                    translate(`Your remark should be lower than :max characters`, {
                        'max': config.components.EstimateLineReader.lineRemarkInput.max.toString(),
                    }),
                );
            }
        },

        validateAndAddLineRemark(lineId: number) {
            this.updateLineRemarkBufferMemory(lineId);
            this.callAddLineRemarkFromBufferMemory(lineId);
        },

        /**
         * Ask the backend to add a line remark from our buffer memory.
         *
         * @param lineId The line id to update
         */
        callAddLineRemarkFromBufferMemory(lineId: number): void {
            const value = this.lineRemarkBuffer.get(lineId);

            // We only add remark when the field is not empty
            if (!this.hasAnyLineRemarkError(lineId)) {
                this.lineRemarkBuffer.delete(lineId);
                this.getLineRemarkInputElementFromLineId(lineId).value = "";

                this.isUpdating = true;
                this.$wire
                    .$call("addLineRemark", lineId, value)
                    .then((data) => {
                        this.isUpdating = false;

                        if ('nothingToAdd' in data) {
                            return;
                        }

                        if ('newRemark' in data && is.object(data.newRemark)) {
                            this.addLineRemark(lineId, data.newRemark as LineRemark);
                        }
                        if ('messageBag' in data && is.object(data.messageBag)) {
                            this.messageBag.updateMainAndLineMessages(lineId, (data.messageBag as MessageBag));
                        }
                        if ('permissions' in data && is.object(data.permissions)) {
                            this.updatePermissions(data.permissions as EstimatePermissions);
                        }
                    });
            }
        },

        /**
         * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
         * Document upload
         * <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<
         */
        /**
         * Show a document from its id
         *
         * @param documentId The document id to open
         */
        openLineDocument(documentId: number): void {
            this.$wire
                .$call("getRouteToShowDocument", documentId)
                .then((data) => {
                    if (!is.object(data) || !is.string(data.url)) {
                        throw new InvalidLivewireResponseFormat('Cannot parse the livewire response to open the document. Got: ' + JSON.stringify(data));
                    }

                    if ('' === data.url) {
                        throw new InvalidLivewireResponseArgument(translate("The document URL is invalid. If the problem persists, please contact an administrator"));
                    }

                    if (is.undefined(window) || !is.function(window.open)) {
                        throw new InvalidWindowContentError("Cannot open the document in a new window. Your browser may not support this feature.");
                    }

                    window.open(data.url, "_blank");
                })
                .catch((e: Error) => {
                    if (e instanceof InvalidLivewireResponseArgument) {
                        notifyErrorAlert(e.message);
                    } else {
                        notifyErrorAlert(translate("An error occurred while trying to open the document. If the problem persists, please contact an administrator"));
                    }

                    throw e;
                });
        },

        /**
         * Triggered when a file has been updated
         *
         * @param lineId The number id that has a file
         * @param filename The document name that was affected to a line
         * @param fileId The id of the attached document
         * @param messageBag The message bag retrieved after the file has been attached
         */
        fileHasBeenAttached(lineId: number, filename: string, documentId: number, messageBag: MessageBag, permissions: EstimatePermissions): void {
            const lineIndex = this.lineBag.getIndexFromId(lineId);
            this.lineBag.setDocument(lineIndex, documentId, filename);

            this.messageBag.updateMainAndLineMessages(lineId, messageBag);
            this.updatePermissions(permissions);
        },

        /**
         * Call a modal that ask to dissociate or not a document from a line.
         *
         * @param documentId The id of the document to remove
         * @param lineId The line impacted
         */
        callDissociateDocumentFromId(documentId: number, lineId: number): void {
            this.isUpdating = true;
            this.$wire
                .$call("openDocumentDeletionModal", documentId, lineId)
                .then(() => {
                    this.isUpdating = false;
                })
                .catch((e: Error) => {
                    this.isUpdating = false;
                    notifyErrorAlert(translate("An error occurred while trying to open the document. If the problem persists, please contact an administrator"));
                    throw e;
                });
        },

        /**
         * Triggered when a document attached to a file has been removed
         *
         * @param lineId The id of the line that has its file deleted
         * @param successMessage A success message to show
         * @param messageBag The message bag retrieved after the file has been attached
         */
        fileHasBeenDissociated(lineId: number, successMessage: string, messageBag: MessageBag): void {
            const lineIndex = this.lineBag.getIndexFromId(lineId);
            notifySuccessAlert(successMessage);
            this.lineBag.removeDocument(lineIndex);
            this.messageBag.updateMainAndLineMessages(lineId, messageBag);
        },

        /**
         * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
         * Estimate remarks
         * <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<
         */

        /**
         * Retrieve all the estimate remarks to display them in the frontend
         */
        getEstimateRemarks(): Array<EstimateRemark> {
            return this.estimateRemarks.all();
        },
        /**
         * Call the livewire method to add a new estimate remark then add this new remark to our bag.
         */
        callAddEstimateRemark(): void {
            if ("" !== this.estimateRemarkUserInput) {
                this.isUpdating = true;
                this.$wire
                    .$call("addEstimateRemark", this.estimateRemarkUserInput)
                    .then((data) => {
                        this.isUpdating = false;

                        // The backend is supposed to send these data

                        // Reset the input field
                        this.estimateRemarkUserInput = "";
                        this.estimateRemarks.add(data.newRemark);

                        this.messageBag.updateMessages(data.messageBag);
                        this.updatePermissions(data.permissions);

                        this.isUpdating = false;
                    });
            }
        },

        /**
         * Remove a line remark from an id
         *
         * @param remarkId The remark id to remove
         */
        callRemoveEstimateRemarkFromId(remarkId: number): void {
            this.isUpdating = true;
            this.$wire
                .$call("deleteEstimateRemark", remarkId)
                .then((data) => {
                    this.isUpdating = false;

                    // The backend is supposed to send these data
                    this.estimateRemarks.delete(remarkId);

                    this.messageBag.updateMessages(data.messageBag);
                    this.updatePermissions(data.permissions);
                });
        },


        /**
         * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
         * canUpdateMainFields
         * <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<
         */
        /**
         * Update a line code line
         *
         * @param lineId The line id to update (in backend)
         * @param lineIndex The line index to update (in frontend)
         */
        updateCodeLine(lineId: number, lineIndex: number): void {
            const value = (this.$el as HTMLInputElement).value;

            // Update internal bag
            this.lineBag.getFromIndex(lineIndex).code_line = value;

            this.isUpdating = true;
            this.$wire
                .$call("updateCodeLine", lineId, value)
                .then((data) => {
                    this.isUpdating = false;

                    this.markLineAsUpdatedFromIndex(lineIndex);

                    // Update internal message bag
                    this.messageBag.updateMainAndLineMessages(lineId, data.messageBag);
                    this.updatePermissions(data.permissions);

                    // Update the current line
                    this.lineBag.replace(lineIndex, data.line);
                });
        },

        /**
         * Update a line designation
         *
         * @param lineId The line id to update (in backend)
         * @param lineIndex The line index to update (in frontend)
         */
        updateDesignation(lineId: number, lineIndex: number): void {
            const value = (this.$el as HTMLInputElement).value;

            // Update internal bag
            this.lineBag.getFromIndex(lineIndex).designation = value;

            this.isUpdating = true;
            this.$wire
                .$call("updateDesignation", lineId, value)
                .then((data) => {
                    this.isUpdating = false;

                    // The backend is supposed to send these data
                    this.markLineAsUpdatedFromIndex(lineIndex);

                    // Update internal message bag
                    this.messageBag.updateMainAndLineMessages(lineId, data.messageBag);
                    this.updatePermissions(data.permissions);

                    this.isUpdating = false;
                });
        },

        /**
         * Update a line price for any kind of price (works from an input element)
         *
         * @param field Name of the updated field
         * @param lineId The line id to update (in backend)
         * @param lineIndex The line index to update (in frontend)
         */
        updatePriceField(field: "supply" | "drop" | "drop_off", lineId: number, lineIndex: number): undefined {
            // Runtime check. VERY IMPORTANT
            const authorizedFields = ["supply", "drop", "drop_off"];
            if (!authorizedFields.includes(field)) {
                throw "You cannot update a field that doesnt exists.";
            }

            // Retrieve input current value
            const el = (this.$el as HTMLInputElement);
            el.value = "" === el.value ? "0" : forceEnglishFloatNotation(el.value);
            const parsedValue = parseFloat(el.value);


            if (!isValidFloat(parsedValue)) {
                // Replace new invalid value by old valid value
                (this.$el as HTMLInputElement).value = (this.lineBag.getFromIndex(lineIndex)[field] ?? 0).toString();
                return;
            }
            // Parse after all the checks

            // Update internal price field
            this.lineBag.updatePrice(lineIndex, field, parsedValue);

            this.isUpdating = true;
            this.$wire
                .$call("updatePrice", field, lineId, parsedValue)
                .then((data) => {
                    this.isUpdating = false;

                    this.markLineAsUpdatedFromIndex(lineIndex);

                    this.updateTotalPanelValues(data.totalPanel);
                    // Register the generated remark
                    this.addLineRemark(lineId, data.newRemark);
                    // Update formated version of the line total price column
                    this.lineBag.getFromIndex(lineIndex).formatted_total = data.formattedTotal;

                    // Update internal message bag
                    this.messageBag.updateMainAndLineMessages(lineId, data.messageBag);
                    this.updatePermissions(data.permissions);

                    this.$wire.$dispatch("calculateGlobalPrice");
                    this.$wire.$dispatch("refreshEstimate");
                });
        },

        /**
         * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
         * Total panel managment
         * <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<
         */
        /**
         * Perform a request to refresh the total panel data.
         */
        async reloadToTalPannel(): Promise<void> {
            const values = await this.$wire.$call(
                "getCalculatedTotalPanelData",
                this.estimateVersion,
            );
            this.updateTotalPanelValues(values);
        },

        /**
         * Update the total panel property.
         */
        updateTotalPanelValues(values: Array<TotalPanelItem>) {
            this.totalPanel = values;
        },
    }),
};
